import React from "react";
import { FileType, LinkType } from "../../types/baseTypes";
import { customSanitize } from "../../utils/customSanitize";
import { Button, ButtonTypes } from "../Button/Button";
import { PopupButton } from "./PopupButton";
import { DownloadButton } from "./DownloadButton";
import type { GetLabel } from "@/getData/fetchData.server";
import type { ActivePopupContent } from "@/context/AppContext";
import { BookingButton } from "./BookingButton";

export type DowloadButtonType = {
  link_target: FileType | false;
  link_text: string;
};

export type ExtendedLinkType = {
  type: "email" | "link" | "download" | "popup" | "telephone" | "booking";
  link: LinkType;
  download: DowloadButtonType;
  email: {
    link_target: string;
    link_text: string;
  };
  pop_up: {
    link_text: string;
    link_aria_label: string;
    modal_type?: "accordion" | "theme-options";
    accordion_categories?: number[] | false;
    accordion_elements?: number[] | null | "";
  };
  telephone: {
    link_target: string;
    link_text: string;
    link_aria_label: string;
  };
  link_label?: string;
};

interface ExtededLinkProps {
  extendedLink: ExtendedLinkType;
  extraClass?: string;
  iconOnly?: boolean;
  btnType: ButtonTypes;
  getLabel: GetLabel;
  popupContent?: ActivePopupContent;
}

export const renderButtonContent = (text: string) => (
  <span
    dangerouslySetInnerHTML={{
      __html: customSanitize(text),
    }}
  />
);

export const ExtendedLink = ({
  extendedLink,
  extraClass = "",
  iconOnly = false,
  btnType = "primary",
  getLabel,
  popupContent,
}: ExtededLinkProps) => {
  const { type, link, email, download, pop_up, telephone, link_label } =
    extendedLink;
  const ariaLabelDownload =
    download?.link_target && download.link_target.url
      ? getLabel("arialabel_download_slider").replace(
          `"$filename"`,
          (download.link_target as FileType).filename,
        )
      : "";

  switch (type) {
    case "link":
      return (
        link && (
          <Button
            type={btnType}
            href={link.url}
            target={link.target}
            aria-label={link.aria_label || link.title}
            className={extraClass}
          >
            {renderButtonContent(link.title)}
          </Button>
        )
      );
    case "booking":
      return <BookingButton extraClass={extraClass} linkLabel={link_label} />;

    case "email":
      return (
        email && (
          <Button
            type={btnType}
            href={`mailto:${email.link_target}`}
            target="_blank"
            className={extraClass}
            aria-label={getLabel("email_title").replace(
              `"$emailadress"`,
              email.link_target,
            )}
          >
            {renderButtonContent(email.link_text)}
          </Button>
        )
      );
    case "telephone":
      return (
        telephone && (
          <Button
            type={btnType}
            href={`tel:${telephone.link_target}`}
            target="_blank"
            className={extraClass}
            aria-label={
              telephone.link_aria_label
                ? telephone.link_aria_label
                : getLabel("extended_link_telephone_aria_label").replace(
                    `$$phonenumber`,
                    telephone.link_target,
                  )
            }
          >
            {renderButtonContent(telephone.link_text)}
          </Button>
        )
      );
    case "popup":
      return (
        <PopupButton
          extraClass={extraClass}
          btnType={btnType}
          ariaLabel={pop_up?.link_aria_label}
          popupContent={popupContent}
        >
          {renderButtonContent(pop_up.link_text)}
        </PopupButton>
      );

    case "download":
      return (
        <DownloadButton
          iconOnly={iconOnly}
          ariaLabel={ariaLabelDownload}
          btnType={btnType}
          extraClass={extraClass}
          linkTargetUrl={download.link_target && download.link_target.url}
        >
          {renderButtonContent(download.link_text)}
        </DownloadButton>
      );
  }
};
